import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import classNames from "classnames";
import { useEffect, useState, useMemo } from "react";
import { useIsTablet } from "../../../hooks/common.hooks";
import { useActiveMenuItem } from "../../../hooks/menuItems.hook";
import { getCurrentPath } from "../../../utils/get-params-value";
import { primaryColor } from "../../../scss/colors.module.scss";
import { Logout } from "../../../svgs";
import { logout } from "../../../utils/auth";
import SideBarItem from "./side-bar-item";
import CollapsibleMenuItem from "./collapsible-menu-item";
import CollapsibleMenu from "./collapsible-menu";

const PREFIX = "DesktopSideBar";

const classes = {
  root: `${PREFIX}-root`,
  expanded: `${PREFIX}-expanded`,
  root2: `${PREFIX}-root2`,
  content: `${PREFIX}-content`,
  expanded2: `${PREFIX}-expanded2`,
  root3: `${PREFIX}-root3`,
};

const Root = styled("div")(() => ({
  [`& .${classes.root}`]: {
    backgroundColor: "transparent",
    border: "1px solid rgba(0, 0, 0, .125)",
    boxShadow: "none",
    color: "#fff",
    "&:not(:last-child)": {
      borderBottom: 0,
    },
    "&:before": {
      display: "none",
    },
  },

  [`& .${classes.expanded}`]: {
    "&:first-of-type": {
      marginBottom: 0,
    },
  },

  [`& .${classes.root2}`]: {
    backgroundColor: "rgba(0, 0, 0, .03)",
    borderBottom: "1px solid rgba(0, 0, 0, .125)",
    marginBottom: -1,
    padding: 0,
    minHeight: 56,
  },

  [`& .${classes.content}`]: {
    "& svg": {
      transform: "rotate(0deg)",
      transition: "transform .2s ease-in-out",
    },
    margin: 0,
  },

  [`& .${classes.content}.MuiAccordionSummary-content`]: {
    margin: 0,
  },

  [`& .${classes.expanded2}`]: {
    "& svg": {
      transform: "rotate(180deg)",
    },
  },

  [`& .${classes.root3}`]: {
    padding: 0,
    backgroundColor: "#fff",
    "& a.side-bar-item": {
      "&:hover": {
        backgroundColor: "#fff",
      },
      padding: "1rem",
      color: "#000",
    },
  },
}));

const DesktopSideBar = ({
  name: fullName = "",
  gitVersion = {},
  sideBarMenuItem = [],
}) => {
  const isTablet = useIsTablet();
  const [logoutExpanded, setLogoutExpanded] = useState(false);
  const [sidebarCollapse, setSidebarCollapse] = useState(true);
  const [isMenuHovered, setIsMenuHovered] = useState(false);
  const { gitDate } = gitVersion;
  const activeTabIndex = useActiveMenuItem(sideBarMenuItem);
  const pathName = getCurrentPath();

  // Check if Conversations is active to show expanded menu
  const isConversationsActive = useMemo(() => {
    return pathName.includes("ai-builder/conversations");
  }, [pathName]);

  // Show menu only when sidebar is expanded (not collapsed) or when menu is hovered
  const shouldShowMenu =
    isConversationsActive && (!sidebarCollapse || isMenuHovered);

  useEffect(() => {
    setLogoutExpanded(false);
  }, [isTablet]);

  const handleLogoutExpanded = () => {
    setLogoutExpanded(!logoutExpanded);
  };
  const handleToggleSidebar = (value) => {
    if (!value) {
      setLogoutExpanded(false);
    }
    setSidebarCollapse(!value);
  };
  const handleLogout = () => {
    logout();
  };

  return (
    <Root
      onMouseLeave={() => handleToggleSidebar(false)}
      onMouseEnter={() => handleToggleSidebar(true)}
      className={classNames("desktop-side-bar", {
        collapsed: sidebarCollapse,
      })}
    >
      <div className="desktop-side-bar-container">
        <Accordion
          className="position-relative"
          square
          expanded={logoutExpanded}
          onChange={!sidebarCollapse ? handleLogoutExpanded : undefined}
          classes={{
            root: classes.root,
            expanded: classes.expanded,
          }}
        >
          <AccordionSummary
            aria-controls="panel3d-content"
            id="panel3d-header"
            classes={{
              root: classes.root2,
              content: classes.content,
              expanded: classes.expanded2,
            }}
          >
            <div className="side-bar-user">
              <div className="side-bar-user__name">
                {sidebarCollapse ? fullName.match(/\b(\w)/g) : fullName}
              </div>
              <ExpandMoreIcon />
            </div>
          </AccordionSummary>
          <AccordionDetails
            className="logout-button-group"
            classes={{
              root: classes.root3,
            }}
          >
            <div className="w-100">
              <div className="w-100" onClick={handleLogout}>
                <SideBarItem
                  icon={<Logout fillcolor={primaryColor} borderColor="#000" />}
                  title="Logout"
                  checked={false}
                  href=""
                />
              </div>
              <div className="code-version">
                <p className="m-0">
                  Last Updated On: {new Date(gitDate).toLocaleString() || " "}
                </p>
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
        <div className="side-bar-item-group">
          <div className="side-bar-item-group__container">
            {sideBarMenuItem.map((sideBarItem, index) => {
              if (sideBarItem.isCollapsible && sideBarItem.subItems) {
                // Mark sub-items as active based on current path
                const subItemsWithActive = sideBarItem.subItems.map(
                  (subItem) => ({
                    ...subItem,
                    isActive: pathName.includes(subItem.href),
                  })
                );

                return (
                  <CollapsibleMenuItem
                    key={sideBarItem.title}
                    icon={sideBarItem.icon}
                    title={sideBarItem.title}
                    href={sideBarItem.href}
                    subItems={subItemsWithActive}
                    isActive={subItemsWithActive.some(
                      (subItem) => subItem.isActive
                    )}
                    sidebarCollapse={sidebarCollapse}
                  />
                );
              }

              return (
                <SideBarItem
                  key={sideBarItem.title}
                  icon={sideBarItem.icon}
                  title={sideBarItem.title}
                  href={sideBarItem.href}
                  isActive={index === activeTabIndex}
                />
              );
            })}
          </div>
        </div>
      </div>
      {isConversationsActive && (
        <CollapsibleMenu
          isVisible={shouldShowMenu}
          sidebarCollapse={sidebarCollapse}
          onNewChatClick={() => {
            // Handle new chat click
            console.log("New chat clicked");
          }}
          onMouseEnter={() => setIsMenuHovered(true)}
          onMouseLeave={() => setIsMenuHovered(false)}
        />
      )}
    </Root>
  );
};

DesktopSideBar.propTypes = {};

export default DesktopSideBar;
